import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Files, GearFill, Printer } from "react-bootstrap-icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ClassStudentPicker from "./ClassStudentPicker";
import ReportCard, {
  buildClipboardPayload,
  getSummaryStorageKey,
  isEditedReportCurrent,
  writeClipboard,
} from "./ReportCard";
import ScoresVisibilityToggle from "./ScoresVisibilityToggle";
import { printReportNodes } from "./printReports";
import {
  getClasses,
  getStudentsForClass,
  getEOYClassReports,
  generateClassEOYv2,
  generateIndividualEOYv2,
  updateEOYReport,
} from "../../services/APIClient";
import "./EOYReportAssistant.css";
import useAcademicYearOptions from "../../utils/useAcademicYearOptions";

const BULK_TOOLBAR_STICKY_OFFSET = 8;
const EOY_REPORT_POLL_SCHEDULE = [
  { durationMs: 30000, intervalMs: 10000 },
  { durationMs: 120000, intervalMs: 20000 },
  { durationMs: 80000, intervalMs: 40000 },
  { durationMs: 180000, intervalMs: 60000 },
];
const SCORES_VISIBILITY_STORAGE_PREFIX = "eoy-report-assistant:scores-visible";
const AI_REPORT_GENERATOR_UNREACHABLE_MESSAGE =
  "The AI Report generator couldn't be reached. Please try again later.";
const EOY_REPORT_GENERATION_FAILED_MESSAGE =
  "We couldn't generate this report. Please try again. If it keeps happening, contact support.";
const EOY_REPORT_REGENERATION_AVAILABLE_MESSAGE =
  "New assessment data is available. Regenerate this report to include the latest results.";
const REPORT_VARIANTS = {
  eoy: {
    title: "EOY Report Assistant",
    subtitle:
      "Generate or review End-of-Year reports from the previous school year.",
    disclaimer:
      "I understand these reports are AI-generated and should be reviewed before sharing with parents.",
    assessmentName: "End of Year Report",
    scoresHeading: "Assessment results the parent report is based on",
    editableLabel:
      "REPORT CAN BE EDITED AND SHARED WITH PARENTS. EDITS ARE SAVED AUTOMATICALLY",
    scoresStoragePrefix: SCORES_VISIBILITY_STORAGE_PREFIX,
    summaryStoragePrefix: "eoy-report-assistant:summary-mode",
    getClassReports: getEOYClassReports,
    generateBatch: generateClassEOYv2,
    generateIndividual: generateIndividualEOYv2,
    updateReport: updateEOYReport,
    showFeedback: false,
    historicalAcademicYear: true,
    historicalReadOnly: false,
  },
};
// const NO_ASSESSMENT_DATA_PATTERN = /no assessment data/i; // superseded: batch success:false is now treated as terminal directly

function getReportStorageScope(report, teacherId, classId) {
  const teacherPart = teacherId || "unknown-teacher";
  const classPart = classId || "unknown-class";
  const studentPart =
    report?.student_id || report?.display_name || "unknown-student";
  return `${teacherPart}:${classPart}:${studentPart}`;
}

function getScoresVisibilityStorageKey(
  storageScope,
  prefix = SCORES_VISIBILITY_STORAGE_PREFIX,
) {
  return `${prefix}:${storageScope || "unknown-report"}`;
}

function getScoresVisibilityStorageKeyForReport(
  report,
  teacherId,
  classId,
  prefix = SCORES_VISIBILITY_STORAGE_PREFIX,
) {
  return getScoresVisibilityStorageKey(
    getReportStorageScope(report, teacherId, classId),
    prefix,
  );
}

export function usesHistoricalAcademicYear(reportConfig = {}) {
  return (
    reportConfig.historicalAcademicYear === true ||
    reportConfig.historicalReadOnly === true
  );
}

function getStoredScoresVisibility(storageKey) {
  try {
    return window.localStorage.getItem(storageKey);
  } catch (err) {
    console.warn("Could not read saved EOY score visibility:", err);
    return null;
  }
}

function saveStoredScoresVisibility(storageKey, isVisible) {
  try {
    window.localStorage.setItem(storageKey, isVisible ? "visible" : "hidden");
  } catch (err) {
    console.warn("Could not save EOY score visibility:", err);
  }
}

export function getClassGradeLevel(classRecord) {
  const name = String(
    classRecord?.name || classRecord?.class_name || "",
  ).toLowerCase();
  const gradeLevel = String(classRecord?.grade_level || "").toLowerCase();
  const juniorPattern = /(^|[^a-z])(jnr|junior|ji)([^a-z]|$)/;
  const seniorPattern = /(^|[^a-z])(snr|senior|si)([^a-z]|$)/;
  const firstClassPattern = /(^|[^a-z0-9])(1st|first)([^a-z0-9]|$)/;

  if (
    classRecord?.grade_level3 ||
    firstClassPattern.test(name) ||
    gradeLevel.includes("1st") ||
    gradeLevel.includes("first")
  ) {
    return "first";
  }

  if (
    classRecord?.grade_level1 ||
    juniorPattern.test(name) ||
    gradeLevel.includes("junior")
  ) {
    return "junior";
  }

  if (
    classRecord?.grade_level2 ||
    seniorPattern.test(name) ||
    gradeLevel.includes("senior")
  ) {
    return "senior";
  }

  return "";
}

function getClassAcademicYear(classRecord) {
  return classRecord?.academicYear || classRecord?.academic_year || "";
}

export function normalizeAcademicYear(academicYear) {
  const yearText = String(academicYear || "").trim();
  const match = yearText.match(/^(\d{2}|\d{4})[-/](\d{2}|\d{4})$/);
  if (!match) return yearText;

  return `${match[1].slice(-2)}-${match[2].slice(-2)}`;
}

export function isClassInAcademicYear(classRecord, academicYear) {
  const classAcademicYear = getClassAcademicYear(classRecord);
  if (!classAcademicYear) return true;

  return normalizeAcademicYear(classAcademicYear) === academicYear;
}

export function isCurrentRealReportClass(classRecord, academicYear) {
  const classAcademicYear = getClassAcademicYear(classRecord);
  return (
    Boolean(classAcademicYear) &&
    normalizeAcademicYear(classAcademicYear) === academicYear &&
    classRecord?.active !== false &&
    classRecord?.deleted !== true &&
    classRecord?.trial_class !== true &&
    classRecord?.trialClass !== true &&
    classRecord?.snapshot !== true
  );
}

export function isHistoricalRealReportClass(classRecord, academicYear) {
  const classAcademicYear = getClassAcademicYear(classRecord);
  return (
    Boolean(classAcademicYear) &&
    normalizeAcademicYear(classAcademicYear) === academicYear &&
    classRecord?.deleted !== true &&
    classRecord?.trial_class !== true &&
    classRecord?.trialClass !== true
  );
}

function getStudentDisplayName(student, fallback) {
  return (
    [student?.firstname, student?.lastname].filter(Boolean).join(" ") ||
    student?.name ||
    fallback
  );
}

function useStickySentinel(offset) {
  const sentinelRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const updateStickyState = () => {
      setIsSticky(sentinel.getBoundingClientRect().top < offset);
    };

    if (!("IntersectionObserver" in window)) {
      updateStickyState();
      window.addEventListener("scroll", updateStickyState, { passive: true });
      window.addEventListener("resize", updateStickyState);

      return () => {
        window.removeEventListener("scroll", updateStickyState);
        window.removeEventListener("resize", updateStickyState);
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [offset]);

  return { isSticky, sentinelRef };
}

function BulkSummaryToggle({ allSummariesEdited, onBulkSummaryModeChange }) {
  return (
    <div className="eoy-bulk-summary-toggle">
      <span className="eoy-summary-toggle-label">AI Generated</span>
      <label
        className="eoy-summary-switch"
        data-tooltip="Switch all report summaries between generated and edited text"
      >
        <input
          type="checkbox"
          aria-label="Switch all report summaries between generated and edited text"
          checked={allSummariesEdited}
          onChange={(event) =>
            onBulkSummaryModeChange(
              event.target.checked ? "edited" : "generated",
            )
          }
        />
        <span className="eoy-summary-slider" />
      </label>
      <span className="eoy-summary-toggle-label">Edited</span>
    </div>
  );
}

function BulkToolbar({
  allSummariesEdited,
  allScoresVisible,
  allCategoriesExpanded,
  hasAnyExpandableGroups,
  bulkCopyStatus,
  onBulkScoresVisibilityChange,
  onBulkSummaryModeChange,
  onBulkCategoriesExpandedChange,
  onCopyAllReports,
  onPrintAllReports,
  reportCount,
  showEvidence = true,
  readOnly = false,
}) {
  const { isSticky, sentinelRef } = useStickySentinel(
    BULK_TOOLBAR_STICKY_OFFSET,
  );

  return (
    <>
      <div className="eoy-sticky-sentinel" ref={sentinelRef} />
      <div
        className={`eoy-sticky-panel eoy-bulk-toolbar no-print ${
          isSticky ? "eoy-sticky-panel-stuck" : ""
        }`}
      >
        <div className="eoy-bulk-toolbar-main">
          <span className="eoy-bulk-toolbar-title">Work with all reports</span>
          <span className="eoy-bulk-toolbar-meta">
            {reportCount} report{reportCount === 1 ? "" : "s"} ready
          </span>
        </div>
        <div className="eoy-bulk-toolbar-actions">
          <span
            className={`eoy-copy-status ${
              bulkCopyStatus ? "" : "eoy-copy-status-hidden"
            }`}
            aria-live="polite"
          >
            {bulkCopyStatus || "Copied all"}
          </span>
          {!readOnly && (
            <div className="eoy-bulk-toolbar-section">
              <BulkSummaryToggle
                allSummariesEdited={allSummariesEdited}
                onBulkSummaryModeChange={onBulkSummaryModeChange}
              />
            </div>
          )}
          {showEvidence && (
            <div className="eoy-bulk-toolbar-section">
              <ScoresVisibilityToggle
                checked={allScoresVisible}
                onChange={onBulkScoresVisibilityChange}
              />
            </div>
          )}
          {showEvidence && allScoresVisible && hasAnyExpandableGroups && (
            <div className="eoy-bulk-toolbar-section">
              <ScoresVisibilityToggle
                label="Expand subtests"
                checked={allCategoriesExpanded}
                tooltip="Expand or collapse all assessment subcategories for all reports"
                onChange={onBulkCategoriesExpandedChange}
              />
            </div>
          )}
          <button
            aria-label="Copy all generated reports to clipboard"
            className="eoy-toolbar-icon-btn eoy-bulk-action-btn"
            disabled={reportCount === 0}
            onClick={onCopyAllReports}
            data-tooltip="Copy all generated reports to clipboard"
            type="button"
          >
            <Files aria-hidden="true" size={15} />
          </button>
          <button
            aria-label="Print all generated reports"
            className="eoy-toolbar-icon-btn eoy-bulk-action-btn"
            disabled={reportCount === 0}
            onClick={onPrintAllReports}
            data-tooltip="Print all generated reports"
            type="button"
          >
            <Printer aria-hidden="true" size={15} />
          </button>
        </div>
      </div>
    </>
  );
}

function GeneratingProgress({ progress }) {
  const { isSticky, sentinelRef } = useStickySentinel(
    BULK_TOOLBAR_STICKY_OFFSET,
  );
  const percentComplete = Math.round((progress.done / progress.total) * 100);

  return (
    <>
      <div className="eoy-sticky-sentinel" ref={sentinelRef} />
      <div
        className={`eoy-sticky-panel eoy-progress-wrap ${
          isSticky ? "eoy-sticky-panel-stuck" : ""
        }`}
      >
        <div className="eoy-progress-header">
          Generating report {Math.min(progress.done + 1, progress.total)} of{" "}
          {progress.total}...
        </div>
        <div className="eoy-progress-message">
          <span className="eoy-alpaca-working" aria-hidden="true">
            <img
              alt=""
              className="eoy-alpaca-working-img"
              src="/assets/imgs/Alpaca-front-01.png"
            />
            <GearFill className="eoy-alpaca-thinking-gear" size={18} />
          </span>
          <span className="eoy-alpaca-speech">
            We are building your reports now. This can take a few minutes while
            we dig through the assessment data, shape each report, and keep
            checking for fresh updates.
          </span>
        </div>
        <div className="eoy-progress-bar">
          <div
            className="eoy-progress-fill"
            style={{ width: `${percentComplete}%` }}
          />
        </div>
        <div className="eoy-progress-meta">
          {progress.done} of {progress.total} complete ({percentComplete}%)
        </div>
      </div>
    </>
  );
}

function getSavedReportsFromResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.reports)) return data.reports;
  if (Array.isArray(data?.saved_reports)) return data.saved_reports;
  if (Array.isArray(data?.savedReports)) return data.savedReports;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

const SAVED_REPORT_TEXT_FIELDS = [
  "report_text",
  "reportText",
  "report_content",
  "reportContent",
  "generated_report",
  "generatedReport",
  "text",
  "content",
];
const GENERATED_REPORT_TEXT_FIELDS = [
  "report_text",
  "reportText",
  "generated_report",
  "generatedReport",
];

function getFirstReportField(record, fieldNames) {
  for (const fieldName of fieldNames) {
    if (record?.[fieldName]) return record[fieldName];
  }

  return "";
}

function hasReportField(record, fieldName) {
  return record?.[fieldName] !== undefined && record?.[fieldName] !== null;
}

function getReportTextFromRecords(records, fieldNames) {
  for (const record of records) {
    const reportText =
      (typeof record?.report === "string" ? record.report : "") ||
      getFirstReportField(record, fieldNames);

    if (reportText) return reportText;
  }

  return "";
}

function getReportFieldFromRecords(fieldName, ...records) {
  const camelFieldName = fieldName.replace(/_([a-z])/g, (_, letter) =>
    letter.toUpperCase(),
  );

  for (const record of records) {
    if (hasReportField(record, fieldName)) return record[fieldName];
    if (hasReportField(record, camelFieldName)) return record[camelFieldName];
  }

  return null;
}

function getReportEditFields(...records) {
  return {
    edited_report: getReportFieldFromRecords("edited_report", ...records),
    edited_at: getReportFieldFromRecords("edited_at", ...records),
    generated_at: getReportFieldFromRecords("generated_at", ...records),
  };
}

function isSuccessfulSavedReport(report) {
  return report.success;
}

function isDisplayableSavedReport(report) {
  return report.success || report.can_regenerate;
}

export function normalizeSavedReport(savedReport) {
  const reportRecord =
    savedReport.report && typeof savedReport.report === "object"
      ? savedReport.report
      : savedReport.saved_report ||
        savedReport.savedReport ||
        savedReport.eoy_report ||
        savedReport.eoyReport ||
        savedReport;
  const studentRecord =
    savedReport.student ||
    reportRecord.student ||
    savedReport.student_data ||
    {};
  const studentId =
    savedReport.student_id ||
    savedReport.studentId ||
    savedReport.student_uuid ||
    savedReport.studentUuid ||
    reportRecord.student_id ||
    reportRecord.studentId ||
    reportRecord.student_uuid ||
    reportRecord.studentUuid ||
    studentRecord.id ||
    savedReport.id;
  const displayName =
    savedReport.display_name ||
    savedReport.displayName ||
    savedReport.student_name ||
    savedReport.studentName ||
    reportRecord.display_name ||
    reportRecord.displayName ||
    reportRecord.student_name ||
    reportRecord.studentName ||
    [studentRecord.firstname, studentRecord.lastname]
      .filter(Boolean)
      .join(" ") ||
    studentRecord.name ||
    studentId;
  const canRegenerate =
    savedReport.can_regenerate === true ||
    savedReport.canRegenerate === true ||
    reportRecord.can_regenerate === true ||
    reportRecord.canRegenerate === true;
  const reportText = getReportTextFromRecords(
    [savedReport, reportRecord],
    SAVED_REPORT_TEXT_FIELDS,
  );

  return {
    ...reportRecord,
    ...savedReport,
    student_id: studentId,
    display_name: displayName,
    report: canRegenerate ? "" : reportText,
    can_regenerate: canRegenerate,
    ...getReportEditFields(savedReport, reportRecord),
    error: canRegenerate
      ? EOY_REPORT_REGENERATION_AVAILABLE_MESSAGE
      : getEOYDisplayFailureReason(savedReport),
    scores:
      savedReport.scores ||
      reportRecord.scores ||
      savedReport.screeners ||
      reportRecord.screeners ||
      [],
    success:
      !canRegenerate && savedReport.success !== false && Boolean(reportText),
    saved: true,
  };
}

async function fetchSavedEOYReports(
  studentIds,
  getClassReports = getEOYClassReports,
  academicYear,
  context,
) {
  const savedReports = await fetchSavedEOYReportStatuses(
    studentIds,
    getClassReports,
    academicYear,
    context,
  );

  return savedReports.filter(isSuccessfulSavedReport);
}

async function fetchDisplayableSavedEOYReports(
  studentIds,
  getClassReports = getEOYClassReports,
  academicYear,
  context,
) {
  const savedReports = await fetchSavedEOYReportStatuses(
    studentIds,
    getClassReports,
    academicYear,
    context,
  );

  return savedReports.filter(isDisplayableSavedReport);
}

async function fetchSavedEOYReportStatuses(
  studentIds,
  getClassReports = getEOYClassReports,
  academicYear,
  context,
) {
  const savedReportsData = await getClassReports(
    studentIds,
    academicYear,
    context,
  );

  return getSavedReportsFromResponse(savedReportsData)
    .filter(Boolean)
    .map(normalizeSavedReport);
}

function getGeneratedReportsFromResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.reports)) return data.reports;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return data ? [data] : [];
}

export function getEOYFailureReason(result) {
  if (!result) return "";
  if (typeof result === "string") return result;

  return (
    result.error ||
    result.response?.data?.error ||
    result.response?.data?.message ||
    result.response?.data?.reason ||
    result.response?.data?.detail ||
    result.response?.data?.failure_reason ||
    result.response?.data?.failureReason ||
    result.message ||
    result.reason ||
    result.detail ||
    result.failure_reason ||
    result.failureReason ||
    ""
  );
}

export function isNoAssessmentFailureReason(reason) {
  return /no assessment/i.test(String(reason || ""));
}

export function isAIReportGeneratorUnavailable(reason) {
  return /(getaddrinfo|enotfound|econnrefused|etimedout|eai_again|network error)/i.test(
    String(reason || ""),
  );
}

export function getEOYDisplayFailureReason(result) {
  const reason = getEOYFailureReason(result);

  if (!reason || isNoAssessmentFailureReason(reason)) return reason;

  return isAIReportGeneratorUnavailable(reason)
    ? AI_REPORT_GENERATOR_UNREACHABLE_MESSAGE
    : EOY_REPORT_GENERATION_FAILED_MESSAGE;
}

function getRegenerateReportErrorMessage(displayName) {
  return `We couldn't regenerate the report for ${displayName}. Please try again.`;
}

// function isNoAssessmentDataFailure(result) {
//   return (
//     !result?.success &&
//     NO_ASSESSMENT_DATA_PATTERN.test(getEOYFailureReason(result))
//   );
// }

export function isEOYReportTerminal(result) {
  return Boolean(
    result?.success ||
    result?.report ||
    (result?.success === false && getEOYFailureReason(result)),
  );
}

function mergeReportsByStudentId(currentReports, nextReports) {
  const mergedByStudentId = new Map();

  [...currentReports, ...nextReports].forEach((report) => {
    if (!report?.student_id) return;
    mergedByStudentId.set(report.student_id, report);
  });

  return Array.from(mergedByStudentId.values());
}

function hasReportForStudent(reportsToCheck, studentId, predicate) {
  return reportsToCheck.some(
    (report) => report.student_id === studentId && predicate(report),
  );
}

function hasSuccessfulReportForStudent(reportsToCheck, studentId) {
  return hasReportForStudent(
    reportsToCheck,
    studentId,
    (report) => report.success,
  );
}

function hasTerminalReportForStudent(reportsToCheck, studentId) {
  return hasReportForStudent(reportsToCheck, studentId, isEOYReportTerminal);
}

function getTerminalReportCount(reportsToCheck, studentIds) {
  const studentIdSet = new Set(studentIds);

  return reportsToCheck.filter(
    (report) =>
      studentIdSet.has(report.student_id) && isEOYReportTerminal(report),
  ).length;
}

function getPendingStudentIds(studentIds, reportsToCheck) {
  return studentIds.filter(
    (studentId) => !hasTerminalReportForStudent(reportsToCheck, studentId),
  );
}

function removeSelectedSuccessfulReports(selectedStudentIds, reportsToCheck) {
  return selectedStudentIds.filter(
    (studentId) => !hasSuccessfulReportForStudent(reportsToCheck, studentId),
  );
}

function normalizeGeneratedReport(report, student) {
  const displayFailureReason = getEOYDisplayFailureReason(report);
  const reportText = getReportTextFromRecords(
    [report],
    GENERATED_REPORT_TEXT_FIELDS,
  );

  return {
    ...report,
    student_id: report.student_id || report.studentId || student.student_id,
    display_name:
      report.display_name || report.displayName || student.display_name,
    report: reportText,
    ...getReportEditFields(report),
    success:
      report.success !== undefined ? report.success : Boolean(reportText),
    ...(displayFailureReason ? { error: displayFailureReason } : {}),
  };
}

function getTerminalGeneratedReports(data, studentsToGenerate) {
  const generatedReportsById = new Map(
    getGeneratedReportsFromResponse(data)
      .map((report) => [report.student_id || report.studentId, report])
      .filter(([studentId]) => Boolean(studentId)),
  );

  return (
    studentsToGenerate
      .map((student) => {
        const report = generatedReportsById.get(student.student_id);
        return report ? normalizeGeneratedReport(report, student) : null;
      })
      // Any result from the batch is definitive — success: false students won't
      // appear in /eoy/reports/class, so polling for them is futile.
      .filter(Boolean)
  );
}

export function shouldRegenerateSavedEOYReport(report) {
  return report?.can_regenerate === true;
}

function getFailedReportsForStudentIds(
  studentIds,
  studentsById,
  failureReason,
) {
  const displayFailureReason = getEOYDisplayFailureReason(failureReason);

  return studentIds.map((studentId) => {
    const student = studentsById.get(studentId);
    return {
      student_id: studentId,
      display_name: getStudentDisplayName(student, studentId),
      success: false,
      error: displayFailureReason,
      report: null,
    };
  });
}

function getNextEOYReportPollInterval(elapsedMs) {
  let windowEndMs = 0;

  for (const scheduleWindow of EOY_REPORT_POLL_SCHEDULE) {
    windowEndMs += scheduleWindow.durationMs;

    if (elapsedMs < windowEndMs) {
      return Math.min(scheduleWindow.intervalMs, windowEndMs - elapsedMs);
    }
  }

  return null;
}

function clearEOYReportPollTimer(timerRef) {
  if (!timerRef.current) return;

  window.clearTimeout(timerRef.current);
  timerRef.current = null;
}

function reportHasExpandableGroups(report) {
  return (report?.scores || []).some((screener) => {
    const countsByCategoryId = new Map();
    (screener.tasks || []).forEach((task) => {
      const categoryId =
        task?.category?.id || task?.parentTaskid || task?.parentTaskId;
      if (categoryId) {
        countsByCategoryId.set(
          categoryId,
          (countsByCategoryId.get(categoryId) || 0) + 1,
        );
      }
    });
    return Array.from(countsByCategoryId.values()).some((count) => count > 1);
  });
}

function startEOYReportPollingRun(runRef, timerRef) {
  const runId = runRef.current + 1;
  runRef.current = runId;
  clearEOYReportPollTimer(timerRef);
  return runId;
}

function waitForEOYReportPollInterval(timeoutRef, intervalMs) {
  return new Promise((resolve) => {
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      resolve();
    }, intervalMs);
  });
}

/**
 * Main panel for the EOY Report Assistant feature.
 */
function EOYReportAssistantPanel({
  teacherId,
  schoolId,
  variant = "eoy",
  displayName = "",
  displaySubheading = "",
}) {
  const baseReportConfig = REPORT_VARIANTS[variant] || REPORT_VARIANTS.eoy;
  const reportConfig = useMemo(
    () =>
      displayName || displaySubheading
        ? {
            ...baseReportConfig,
            ...(displayName ? {title: displayName} : {}),
            ...(displaySubheading ? {subtitle: displaySubheading} : {}),
          }
        : baseReportConfig,
    [baseReportConfig, displayName, displaySubheading],
  );
  const isEnhancedReport = variant !== "eoy";
  const getClassReports = reportConfig.getClassReports;
  const generateBatch = reportConfig.generateBatch;
  const generateIndividual = reportConfig.generateIndividual;
  const [selectedScreeningPoint, setSelectedScreeningPoint] = useState("");
  const [availableScreeningPointOptions, setAvailableScreeningPointOptions] =
    useState([]);
  const [screeningPointContextLoading, setScreeningPointContextLoading] =
    useState(false);
  const [screeningPointContextError, setScreeningPointContextError] =
    useState("");
  const [selectedReportAcademicYear, setSelectedReportAcademicYear] =
    useState("");
  const requiresScreeningPoint =
    (reportConfig.screeningPointOptions || []).length > 0;
  const reportContextReady =
    !requiresScreeningPoint ||
    (Boolean(selectedScreeningPoint) && !screeningPointContextLoading);
  const useHistoricalAcademicYear = usesHistoricalAcademicYear(reportConfig);
  const academicYearOptions = useAcademicYearOptions({
    previousYears:
      useHistoricalAcademicYear || reportConfig.academicYearSelectable
        ? 1
        : 0,
  });
  const currentAcademicYearOption = academicYearOptions[0];
  const reportAcademicYearOption = reportConfig.academicYearSelectable
    ? academicYearOptions.find(
        (option) => option.storage === selectedReportAcademicYear,
      ) || currentAcademicYearOption
    : useHistoricalAcademicYear
      ? academicYearOptions[1] || currentAcademicYearOption
      : currentAcademicYearOption;
  const reportAcademicYear = reportAcademicYearOption.storage;
  const reportRequestContext = useMemo(
    () => ({
      ...(selectedScreeningPoint
        ? { screening_point: selectedScreeningPoint }
        : {}),
      ...(isEnhancedReport
        ? reportConfig.academicYearSelectable
          ? { academic_year: reportAcademicYear }
          : {}
        : { academicYear: reportAcademicYear }),
    }),
    [
      isEnhancedReport,
      reportAcademicYear,
      reportConfig.academicYearSelectable,
      selectedScreeningPoint,
    ],
  );
  const fetchSavedReports = useCallback(
    (studentIds) =>
      reportContextReady
        ? fetchSavedEOYReports(
            studentIds,
            getClassReports,
            reportAcademicYear,
            reportRequestContext,
          )
        : Promise.resolve([]),
    [
      getClassReports,
      reportAcademicYear,
      reportContextReady,
      reportRequestContext,
    ],
  );
  const fetchDisplayableSavedReports = useCallback(
    (studentIds) =>
      reportConfig.historicalReadOnly
        ? fetchSavedEOYReportStatuses(
            studentIds,
            getClassReports,
            reportAcademicYear,
            reportRequestContext,
          )
        : fetchDisplayableSavedEOYReports(
            studentIds,
            getClassReports,
            reportAcademicYear,
            reportRequestContext,
          ),
    [
      getClassReports,
      reportAcademicYear,
      reportConfig.historicalReadOnly,
      reportRequestContext,
    ],
  );
  const fetchSavedReportStatuses = useCallback(
    (studentIds) =>
      fetchSavedEOYReportStatuses(
        studentIds,
        getClassReports,
        reportAcademicYear,
        reportRequestContext,
      ),
    [getClassReports, reportAcademicYear, reportRequestContext],
  );
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [classesError, setClassesError] = useState(null);

  const [selectedGradeLevel, setSelectedGradeLevel] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedClassName, setSelectedClassName] = useState("");
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);

  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [regeneratingStudentIds, setRegeneratingStudentIds] = useState([]);
  const [savedReportsLoading, setSavedReportsLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [reports, setReports] = useState([]);
  const [genError, setGenError] = useState(null);
  const [reportsSource, setReportsSource] = useState(null);
  const [bulkCopyStatus, setBulkCopyStatus] = useState("");
  const [scoresVisibleByKey, setScoresVisibleByKey] = useState({});
  const [summaryModeByKey, setSummaryModeByKey] = useState({});
  const [allCategoriesExpanded, setAllCategoriesExpanded] = useState(false);
  const reportNodeRefs = useRef(new Map());
  const activePrintCleanupRef = useRef(null);
  const bulkCopyStatusTimerRef = useRef(null);
  const reportPollingRunRef = useRef(0);
  const reportPollingTimerRef = useRef(null);

  const resetGeneratedReportState = useCallback(() => {
    setReports([]);
    setReportsSource(null);
    setGenError(null);
    setGenerating(false);
    setProgress({ done: 0, total: 0 });
  }, []);

  const currentAcademicYear = reportAcademicYear;
  const successfulReports = useMemo(
    () => reports.filter((report) => report.success),
    [reports],
  );
  const unavailableStudentIds = useMemo(
    () => successfulReports.map((report) => report.student_id).filter(Boolean),
    [successfulReports],
  );
  const unavailableStudentIdSet = useMemo(
    () => new Set(unavailableStudentIds),
    [unavailableStudentIds],
  );
  const filteredClasses = useMemo(
    () =>
      selectedGradeLevel
        ? classes.filter(
            (classRecord) =>
              getClassGradeLevel(classRecord) === selectedGradeLevel,
          )
        : [],
    [classes, selectedGradeLevel],
  );
  const successfulReportScoreKeys = useMemo(
    () =>
      successfulReports.map((report) =>
        getScoresVisibilityStorageKeyForReport(
          report,
          teacherId,
          selectedClassId,
          reportConfig.scoresStoragePrefix,
        ),
      ),
    [
      reportConfig.scoresStoragePrefix,
      selectedClassId,
      successfulReports,
      teacherId,
    ],
  );
  const allScoresVisible =
    successfulReportScoreKeys.length > 0 &&
    successfulReportScoreKeys.every(
      (scoreKey) => scoresVisibleByKey[scoreKey] !== false,
    );
  const hasAnyExpandableGroups = useMemo(
    () => successfulReports.some(reportHasExpandableGroups),
    [successfulReports],
  );
  const successfulReportSummaryKeys = useMemo(
    () =>
      successfulReports.map((report) =>
        getSummaryStorageKey(
          teacherId,
          selectedClassId,
          report,
          reportConfig.summaryStoragePrefix,
        ),
      ),
    [
      reportConfig.summaryStoragePrefix,
      selectedClassId,
      successfulReports,
      teacherId,
    ],
  );
  const allSummariesEdited =
    successfulReportSummaryKeys.length > 0 &&
    successfulReportSummaryKeys.every(
      (summaryKey) => summaryModeByKey[summaryKey] === "edited",
    );

  useEffect(() => {
    const nextScoresVisibleByKey = {};

    successfulReports.forEach((report) => {
      const scoreKey = getScoresVisibilityStorageKeyForReport(
        report,
        teacherId,
        selectedClassId,
        reportConfig.scoresStoragePrefix,
      );
      const storedState = getStoredScoresVisibility(scoreKey);
      nextScoresVisibleByKey[scoreKey] = storedState
        ? storedState === "visible"
        : true;
    });

    setScoresVisibleByKey(nextScoresVisibleByKey);
  }, [
    reportConfig.scoresStoragePrefix,
    selectedClassId,
    successfulReports,
    teacherId,
  ]);

  useEffect(() => {
    const nextSummaryModeByKey = {};

    successfulReports.forEach((report) => {
      const summaryKey = getSummaryStorageKey(
        teacherId,
        selectedClassId,
        report,
        reportConfig.summaryStoragePrefix,
      );
      nextSummaryModeByKey[summaryKey] = isEditedReportCurrent(report)
        ? "edited"
        : "generated";
    });

    setSummaryModeByKey(nextSummaryModeByKey);
  }, [
    reportConfig.summaryStoragePrefix,
    selectedClassId,
    successfulReports,
    teacherId,
  ]);

  // Load classes (filtered to current academic year)
  useEffect(() => {
    let cancelled = false;

    async function loadClasses() {
      setClassesLoading(true);
      setClassesError(null);
      try {
        const data = await getClasses();
        if (cancelled) return;
        const allClasses = Array.isArray(data) ? data : data?.classes || [];

        const currentYearClasses = allClasses.filter((classRecord) =>
          isEnhancedReport
            ? reportConfig.academicYearSelectable
              ? isHistoricalRealReportClass(classRecord, currentAcademicYear)
              : isCurrentRealReportClass(classRecord, currentAcademicYear)
            : isClassInAcademicYear(classRecord, currentAcademicYear),
        );

        console.log(
          `[EOY] Loaded ${allClasses.length} classes, ${currentYearClasses.length} in academic year ${currentAcademicYear}`,
        );
        setClasses(currentYearClasses);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load classes:", err);
        setClassesError("Could not load your classes. Please try refreshing.");
      } finally {
        if (!cancelled) setClassesLoading(false);
      }
    }

    loadClasses();
    return () => {
      cancelled = true;
    };
  }, [
    currentAcademicYear,
    isEnhancedReport,
    reportConfig.academicYearSelectable,
  ]);

  useEffect(() => {
    if (!isEnhancedReport || selectedGradeLevel || classes.length === 0) return;
    const grades = [
      ...new Set(classes.map(getClassGradeLevel).filter(Boolean)),
    ];
    if (grades.length === 1) setSelectedGradeLevel(grades[0]);
  }, [classes, isEnhancedReport, selectedGradeLevel]);

  useEffect(() => {
    if (
      !isEnhancedReport ||
      !selectedGradeLevel ||
      selectedClassId ||
      filteredClasses.length !== 1
    ) {
      return;
    }
    const onlyClass = filteredClasses[0];
    setSelectedClassId(onlyClass.id);
    setSelectedClassName(onlyClass.name || "");
  }, [
    filteredClasses,
    isEnhancedReport,
    selectedClassId,
    selectedGradeLevel,
  ]);

  // Load students when class is picked
  useEffect(() => {
    if (!selectedClassId) {
      setStudents([]);
      setSelectedStudentIds([]);
      setReports([]);
      setReportsSource(null);
      setStudentsLoading(false);
      setSavedReportsLoading(false);
      return;
    }

    let cancelled = false;

    async function loadClassData() {
      setStudentsLoading(true);
      setSavedReportsLoading(true);
      setStudentsError(null);
      setSelectedStudentIds([]);
      setReports([]);
      setReportsSource(null);

      try {
        const studentsData = await getStudentsForClass(selectedClassId);
        if (cancelled) return;
        const list = studentsData?.students || studentsData || [];
        const classStudents = Array.isArray(list) ? list : [];
        setStudents(classStudents);

        if (!reportContextReady) return;

        const savedReports = await fetchDisplayableSavedReports(
          classStudents.map((student) => student.id).filter(Boolean),
        ).catch((err) => {
          console.warn("Failed to load saved EOY reports:", err);
          return [];
        });
        if (cancelled) return;

        if (savedReports.length > 0) {
          setReports(savedReports);
          setReportsSource("saved");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load students:", err);
        setStudentsError("Could not load students for this class.");
      } finally {
        if (!cancelled) {
          setStudentsLoading(false);
          setSavedReportsLoading(false);
        }
      }
    }

    loadClassData();
    return () => {
      cancelled = true;
    };
  }, [fetchDisplayableSavedReports, reportContextReady, selectedClassId]);

  const handleGradeLevelChange = (gradeLevel) => {
    startEOYReportPollingRun(reportPollingRunRef, reportPollingTimerRef);
    setSelectedGradeLevel(gradeLevel);
    setSelectedClassId("");
    setSelectedClassName("");
    setSelectedScreeningPoint("");
    setStudents([]);
    setSelectedStudentIds([]);
    setDisclaimerAccepted(false);
    resetGeneratedReportState();
  };

  const handleClassChange = (classId) => {
    startEOYReportPollingRun(reportPollingRunRef, reportPollingTimerRef);
    setSelectedClassId(classId);
    const cls = classes.find((c) => String(c.id) === String(classId));
    setSelectedClassName(cls?.name || "");
    setSelectedScreeningPoint("");
    setDisclaimerAccepted(false);
    resetGeneratedReportState();
  };

  const handleAcademicYearChange = (academicYear) => {
    startEOYReportPollingRun(reportPollingRunRef, reportPollingTimerRef);
    setSelectedReportAcademicYear(academicYear);
    setSelectedGradeLevel("");
    setSelectedClassId("");
    setSelectedClassName("");
    setSelectedScreeningPoint("");
    setStudents([]);
    setSelectedStudentIds([]);
    setDisclaimerAccepted(false);
    resetGeneratedReportState();
  };

  const handleScreeningPointChange = (screeningPoint) => {
    startEOYReportPollingRun(reportPollingRunRef, reportPollingTimerRef);
    setSelectedScreeningPoint(screeningPoint);
    setSelectedStudentIds([]);
    setDisclaimerAccepted(false);
    resetGeneratedReportState();
  };

  const handleStudentToggle = (studentId) => {
    if (unavailableStudentIdSet.has(studentId)) return;

    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const handleSelectAll = () => {
    const selectableStudentIds = students
      .map((student) => student.id)
      .filter((studentId) => !unavailableStudentIdSet.has(studentId));

    if (selectedStudentIds.length === selectableStudentIds.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(selectableStudentIds);
    }
  };

  const handleReportNodeReady = useCallback((studentId, node) => {
    if (!studentId) return;
    if (node) {
      reportNodeRefs.current.set(studentId, node);
    } else {
      reportNodeRefs.current.delete(studentId);
    }
  }, []);

  const handlePrintAllReports = useCallback(() => {
    const reportNodes = successfulReports
      .map((report) => reportNodeRefs.current.get(report.student_id))
      .filter(Boolean);

    if (reportNodes.length === 0) return;

    activePrintCleanupRef.current?.();
    activePrintCleanupRef.current = printReportNodes(reportNodes);
  }, [successfulReports]);

  const buildBulkClipboardPayload = useCallback(() => {
    const payloads = successfulReports
      .map((report) => reportNodeRefs.current.get(report.student_id))
      .filter(Boolean)
      .map((node) => buildClipboardPayload(node))
      .filter(Boolean);

    if (payloads.length === 0) return null;

    const html = payloads
      .map((payload, index) => {
        const pageBreak =
          index === 0
            ? ""
            : '<div style="break-before: page; page-break-before: always; font-size: 1px; line-height: 1px; height: 1px;">&nbsp;</div>';
        return `${pageBreak}${payload.html}`;
      })
      .join("");

    return {
      html,
      text: payloads.map((payload) => payload.text).join("\n\n\f\n\n"),
    };
  }, [successfulReports]);

  const handleCopyAllReports = useCallback(async () => {
    try {
      const copied = await writeClipboard(buildBulkClipboardPayload());
      setBulkCopyStatus(copied ? "Copied all" : "Copy failed");
    } catch (err) {
      console.warn("Could not copy all EOY reports:", err);
      setBulkCopyStatus("Copy failed");
    }

    if (bulkCopyStatusTimerRef.current) {
      window.clearTimeout(bulkCopyStatusTimerRef.current);
    }
    bulkCopyStatusTimerRef.current = window.setTimeout(() => {
      setBulkCopyStatus("");
    }, 1800);
  }, [buildBulkClipboardPayload]);

  const handleScoresVisibilityChange = useCallback((scoreKey, isVisible) => {
    saveStoredScoresVisibility(scoreKey, isVisible);
    setScoresVisibleByKey((current) => ({
      ...current,
      [scoreKey]: isVisible,
    }));
  }, []);

  const handleBulkScoresVisibilityChange = useCallback(
    (isVisible) => {
      successfulReportScoreKeys.forEach((scoreKey) => {
        saveStoredScoresVisibility(scoreKey, isVisible);
      });
      setScoresVisibleByKey((current) => {
        const next = { ...current };
        successfulReportScoreKeys.forEach((scoreKey) => {
          next[scoreKey] = isVisible;
        });
        return next;
      });
    },
    [successfulReportScoreKeys],
  );

  const handleSummaryModeChange = useCallback((summaryKey, nextSummaryMode) => {
    setSummaryModeByKey((current) => ({
      ...current,
      [summaryKey]: nextSummaryMode,
    }));
  }, []);

  const handleBulkSummaryModeChange = useCallback(
    (nextSummaryMode) => {
      setSummaryModeByKey((current) => {
        const next = { ...current };
        successfulReportSummaryKeys.forEach((summaryKey) => {
          next[summaryKey] = nextSummaryMode;
        });
        return next;
      });
    },
    [successfulReportSummaryKeys],
  );

  const handleBulkCategoriesExpandedChange = useCallback((expanded) => {
    setAllCategoriesExpanded(expanded);
  }, []);

  useEffect(() => {
    return () => {
      startEOYReportPollingRun(reportPollingRunRef, reportPollingTimerRef);
      activePrintCleanupRef.current?.();
      if (bulkCopyStatusTimerRef.current) {
        window.clearTimeout(bulkCopyStatusTimerRef.current);
      }
    };
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!disclaimerAccepted || !reportContextReady) return;

    const selectedIdsToGenerate = selectedStudentIds.filter(
      (studentId) => !unavailableStudentIdSet.has(studentId),
    );

    if (selectedIdsToGenerate.length === 0) return;

    const pollRunId = startEOYReportPollingRun(
      reportPollingRunRef,
      reportPollingTimerRef,
    );

    setGenerating(true);
    setGenError(null);
    setReportsSource(null);
    setProgress({ done: 0, total: selectedIdsToGenerate.length });

    const studentsById = new Map(students.map((s) => [s.id, s]));
    let idsToGenerate = selectedIdsToGenerate;
    let collected = [...reports];

    try {
      const savedReports = await fetchSavedReports(selectedIdsToGenerate);
      if (reportPollingRunRef.current !== pollRunId) return;

      const savedStudentIds = savedReports
        .map((report) => report.student_id)
        .filter(Boolean);

      if (savedReports.length > 0) {
        collected = mergeReportsByStudentId(collected, savedReports);
        setReports(collected);
        setReportsSource("saved");
        setSelectedStudentIds((current) =>
          current.filter((studentId) => !savedStudentIds.includes(studentId)),
        );
        setProgress({
          done: savedReports.length,
          total: selectedIdsToGenerate.length,
        });
        idsToGenerate = selectedIdsToGenerate.filter(
          (studentId) => !savedStudentIds.includes(studentId),
        );
      }

      if (idsToGenerate.length === 0) {
        setGenerating(false);
        return;
      }
    } catch (err) {
      if (reportPollingRunRef.current !== pollRunId) return;

      console.error(
        "Could not check for saved EOY reports before generation:",
        err,
      );
      const failureReason = getEOYDisplayFailureReason(err);
      setGenError(
        failureReason === AI_REPORT_GENERATOR_UNREACHABLE_MESSAGE
          ? failureReason
          : "Could not check for saved reports, so no new reports were generated. Please try again.",
      );
      setGenerating(false);
      setProgress({ done: 0, total: 0 });
      return;
    }

    const studentsToGenerate = idsToGenerate.map((studentId) => {
      const student = studentsById.get(studentId);
      return {
        student_id: studentId,
        display_name: getStudentDisplayName(student, studentId),
      };
    });

    setReports(collected);
    setReportsSource("generated");

    const updateGeneratedReports = (nextReports) => {
      if (nextReports.length === 0) return;

      collected = mergeReportsByStudentId(collected, nextReports);
      setProgress({
        done: getTerminalReportCount(collected, selectedIdsToGenerate),
        total: selectedIdsToGenerate.length,
      });
      setReports(collected);
      setReportsSource("generated");
      setSelectedStudentIds((current) =>
        removeSelectedSuccessfulReports(current, collected),
      );
    };

    let generationRequestFailed = false;
    const generatedStudentIds = studentsToGenerate.map(
      (student) => student.student_id,
    );

    generateBatch({
      students: studentsToGenerate,
      acknowledged: true,
      ...reportRequestContext,
    })
      .then((data) => {
        if (reportPollingRunRef.current !== pollRunId) return;

        const terminalReports = getTerminalGeneratedReports(
          data,
          studentsToGenerate,
        );
        if (terminalReports.length > 0) {
          updateGeneratedReports(terminalReports);
          return;
        }

        const failureReason = getEOYFailureReason(data);
        if (!failureReason) return;

        generationRequestFailed = true;
        const failedStudentIds = getPendingStudentIds(
          generatedStudentIds,
          collected,
        );
        updateGeneratedReports(
          getFailedReportsForStudentIds(
            failedStudentIds,
            studentsById,
            failureReason,
          ),
        );
        setProgress({
          done: selectedIdsToGenerate.length,
          total: selectedIdsToGenerate.length,
        });
        setGenerating(false);
      })
      .catch((err) => {
        if (reportPollingRunRef.current !== pollRunId) return;

        generationRequestFailed = true;
        console.error("Bulk generation failed:", err);
        const failureReason = getEOYFailureReason(err) || "Request failed";
        const failedStudentIds = getPendingStudentIds(
          generatedStudentIds,
          collected,
        );
        updateGeneratedReports(
          getFailedReportsForStudentIds(
            failedStudentIds,
            studentsById,
            failureReason,
          ),
        );
        setProgress({
          done: selectedIdsToGenerate.length,
          total: selectedIdsToGenerate.length,
        });
        setGenerating(false);
      });

    let pendingStudentIds = getPendingStudentIds(
      generatedStudentIds,
      collected,
    );
    let pollElapsedMs = 0;

    while (pendingStudentIds.length > 0) {
      if (generationRequestFailed) break;

      const pollIntervalMs = getNextEOYReportPollInterval(pollElapsedMs);
      if (pollIntervalMs === null) break;

      await waitForEOYReportPollInterval(reportPollingTimerRef, pollIntervalMs);
      pollElapsedMs += pollIntervalMs;
      if (reportPollingRunRef.current !== pollRunId) return;
      if (generationRequestFailed) break;

      try {
        const savedReportStatuses =
          await fetchSavedReportStatuses(pendingStudentIds);
        if (reportPollingRunRef.current !== pollRunId) return;

        const terminalReports = savedReportStatuses.filter(isEOYReportTerminal);

        updateGeneratedReports(terminalReports);
      } catch (err) {
        console.warn("Could not poll saved EOY reports:", err);
      }

      pendingStudentIds = getPendingStudentIds(pendingStudentIds, collected);
      setProgress({
        done: getTerminalReportCount(collected, selectedIdsToGenerate),
        total: selectedIdsToGenerate.length,
      });
    }

    if (reportPollingRunRef.current !== pollRunId) return;

    if (pendingStudentIds.length > 0 && !generationRequestFailed) {
      updateGeneratedReports(
        pendingStudentIds.map((studentId) => {
          const student = studentsById.get(studentId);
          return {
            student_id: studentId,
            display_name: getStudentDisplayName(student, studentId),
            success: false,
            error:
              "No report was returned for this student. Please try regenerating this report.",
            report: null,
          };
        }),
      );
      setProgress({
        done: selectedIdsToGenerate.length,
        total: selectedIdsToGenerate.length,
      });
      startEOYReportPollingRun(reportPollingRunRef, reportPollingTimerRef);
    }

    setGenerating(false);
    setSelectedStudentIds((current) =>
      removeSelectedSuccessfulReports(current, collected),
    );
  }, [
    reports,
    disclaimerAccepted,
    fetchSavedReportStatuses,
    fetchSavedReports,
    generateBatch,
    reportContextReady,
    reportRequestContext,
    selectedStudentIds,
    students,
    unavailableStudentIdSet,
  ]);

  const handleRegenerateReport = useCallback(
    async (failedReport) => {
      const studentId = failedReport?.student_id;
      if (!studentId || regeneratingStudentIds.includes(studentId)) return;
      if (!disclaimerAccepted) {
        setGenError(
          "Please confirm the AI-report acknowledgement before regenerating this report.",
        );
        return;
      }

      setRegeneratingStudentIds((current) => [...current, studentId]);
      setGenError(null);

      const student = students.find((item) => item.id === studentId);
      const displayName =
        failedReport.display_name || getStudentDisplayName(student, studentId);

      try {
        const savedReport = (await fetchSavedReports([studentId]))[0];

        if (savedReport) {
          setReports((current) =>
            current.map((report) =>
              report.student_id === studentId ? savedReport : report,
            ),
          );
          setReportsSource("generated");
          setSelectedStudentIds((current) =>
            current.filter((id) => id !== studentId),
          );
          toast.success(`Report ready for ${displayName}.`);
          return;
        }

        const result = await generateIndividual({
          student_id: studentId,
          regenerate: shouldRegenerateSavedEOYReport(failedReport),
          acknowledged: true,
          ...reportRequestContext,
        });
        const normalizedResult = normalizeGeneratedReport(result, {
          student_id: studentId,
          display_name: displayName,
        });

        setReports((current) =>
          current.map((report) =>
            report.student_id === studentId ? normalizedResult : report,
          ),
        );
        setReportsSource("generated");
        if (normalizedResult.success) {
          setSelectedStudentIds((current) =>
            current.filter((id) => id !== studentId),
          );
          toast.success(`Report regenerated for ${displayName}.`);
        } else {
          toast.error(getRegenerateReportErrorMessage(displayName));
        }
      } catch (err) {
        console.error("Regeneration failed for", studentId, err);
        const failureReason = getEOYDisplayFailureReason(err);
        setReports((current) =>
          current.map((report) =>
            report.student_id === studentId
              ? {
                  ...report,
                  error: failureReason || "Regeneration failed",
                  success: false,
                }
              : report,
          ),
        );
        toast.error(getRegenerateReportErrorMessage(displayName));
      } finally {
        setRegeneratingStudentIds((current) =>
          current.filter((id) => id !== studentId),
        );
      }
    },
    [
      fetchSavedReports,
      disclaimerAccepted,
      generateIndividual,
      regeneratingStudentIds,
      reportRequestContext,
      students,
    ],
  );

  return (
    <div className="eoy-panel-root">
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="eoy-header">
        <h1 className="eoy-title">{reportConfig.title}</h1>
        <p className="eoy-subtitle">{reportConfig.subtitle}</p>
      </div>

      <ClassStudentPicker
        classes={filteredClasses}
        classesLoading={classesLoading}
        classesError={classesError}
        selectedGradeLevel={selectedGradeLevel}
        onGradeLevelChange={handleGradeLevelChange}
        selectedClassId={selectedClassId}
        onClassChange={handleClassChange}
        academicYearOptions={
          reportConfig.academicYearSelectable ? academicYearOptions : []
        }
        selectedAcademicYear={reportAcademicYear}
        onAcademicYearChange={handleAcademicYearChange}
        screeningPointOptions={
          requiresScreeningPoint ? availableScreeningPointOptions : []
        }
        screeningPointRequired={requiresScreeningPoint}
        screeningPointLoading={screeningPointContextLoading}
        screeningPointError={screeningPointContextError}
        selectedScreeningPoint={selectedScreeningPoint}
        onScreeningPointChange={handleScreeningPointChange}
        students={students}
        studentsLoading={studentsLoading}
        studentsError={studentsError}
        selectedStudentIds={selectedStudentIds}
        unavailableStudentIds={unavailableStudentIds}
        onStudentToggle={handleStudentToggle}
        onSelectAll={handleSelectAll}
        disclaimerAccepted={disclaimerAccepted}
        onDisclaimerAcceptedChange={setDisclaimerAccepted}
        onGenerate={handleGenerate}
        generating={generating || savedReportsLoading}
        disclaimerText={reportConfig.disclaimer}
        viewOnly={reportConfig.historicalReadOnly === true}
        academicYearLabel={reportAcademicYearOption.display}
      />

      {savedReportsLoading && selectedClassId && (
        <div className="eoy-muted eoy-saved-reports-status">
          Checking for saved reports...
        </div>
      )}

      {generating && progress.total > 0 && (
        <GeneratingProgress progress={progress} />
      )}

      {!generating && reports.length > 0 && (
        <div className="eoy-done-summary">
          <strong>
            {reportsSource === "saved"
              ? `${reports.length} saved report${reports.length === 1 ? "" : "s"} loaded`
              : `${reports.filter((r) => r.success).length} of ${reports.length} reports generated`}
          </strong>
          {selectedClassName && <span> for {selectedClassName}</span>}
        </div>
      )}

      {genError && <div className="eoy-error">{genError}</div>}

      {!generating && successfulReports.length > 0 && (
        <BulkToolbar
          allSummariesEdited={allSummariesEdited}
          allScoresVisible={allScoresVisible}
          allCategoriesExpanded={allCategoriesExpanded}
          hasAnyExpandableGroups={hasAnyExpandableGroups}
          bulkCopyStatus={bulkCopyStatus}
          onBulkScoresVisibilityChange={handleBulkScoresVisibilityChange}
          onBulkSummaryModeChange={handleBulkSummaryModeChange}
          onBulkCategoriesExpandedChange={handleBulkCategoriesExpandedChange}
          onCopyAllReports={handleCopyAllReports}
          onPrintAllReports={handlePrintAllReports}
          reportCount={successfulReports.length}
          showEvidence={reportConfig.showEvidence !== false}
          readOnly={
            reportConfig.historicalReadOnly === true ||
            reportConfig.reportReadOnly === true
          }
        />
      )}

      {reports.map((report) => {
        const summaryKey = getSummaryStorageKey(
          teacherId,
          selectedClassId,
          report,
          reportConfig.summaryStoragePrefix,
        );
        const scoreKey = getScoresVisibilityStorageKeyForReport(
          report,
          teacherId,
          selectedClassId,
          reportConfig.scoresStoragePrefix,
        );
        return (
          <ReportCard
            key={report.student_id}
            report={report}
            classId={selectedClassId}
            className={selectedClassName}
            academicYear={currentAcademicYear}
            showScores={
              reportConfig.showEvidence !== false &&
              scoresVisibleByKey[scoreKey] !== false
            }
            onScoresVisibilityChange={(isVisible) =>
              handleScoresVisibilityChange(scoreKey, isVisible)
            }
            summaryMode={summaryModeByKey[summaryKey]}
            onSummaryModeChange={(nextSummaryMode) =>
              handleSummaryModeChange(summaryKey, nextSummaryMode)
            }
            expandAllCategories={allCategoriesExpanded}
            onReportNodeReady={handleReportNodeReady}
            onRegenerateReport={
              reportConfig.historicalReadOnly
                ? undefined
                : handleRegenerateReport
            }
            regenerating={regeneratingStudentIds.includes(report.student_id)}
            updateReport={reportConfig.updateReport}
            assessmentName={reportConfig.assessmentName}
            scoresHeading={reportConfig.scoresHeading}
            showSourceEoyReport={false}
            showApprovedNotes={reportConfig.showApprovedNotes === true}
            approvedNotesHeading={reportConfig.approvedNotesHeading}
            approvedNotesDescription={reportConfig.approvedNotesDescription}
            notesAfterReport={reportConfig.notesAfterReport === true}
            reportHeading={reportConfig.reportHeading}
            reportHeadingDescription={reportConfig.reportHeadingDescription}
            editableLabel={reportConfig.editableLabel}
            showFeedback={reportConfig.showFeedback}
            readOnly={
              reportConfig.historicalReadOnly === true ||
              reportConfig.reportReadOnly === true
            }
            reportLayout={reportConfig.reportLayout}
            printLayout={reportConfig.printLayout}
          />
        );
      })}
    </div>
  );
}

export default EOYReportAssistantPanel;
